import { useNavigation } from '@react-navigation/native';
import { Button, theme } from 'aimo-ui';
import Icon from 'components/icons/Icon';
import ParkingSignInButton from 'components/park/ParkingSignInButton';
import ParkingContext from 'context/ParkingContext';
import useIsLoggedIn from 'hooks/useIsLoggedIn';
import { useParkingButtonLogic } from 'hooks/useParkingButton';
import useParkingZoneChangeCountry from 'hooks/useParkingZoneChangeCountry';
import React, { useCallback, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { useParkingStore } from 'stores/parkingStore';
import { useParkzoneStore } from 'stores/parkzoneStore';
import { useSearchStore } from 'stores/searchStore';
import { StyledWrapper } from 'styles/ContainerStyles';
import { ParkNavProps } from 'types/navigation';
import { testIdentifiers } from 'utils/testIdentifiers';

const ChargeIconComponent = () => (
  <Icon name="Charging" color={theme.colors.white} />
);

const ParkIconComponent = () => (
  <Icon name="Park-Outlined" color={theme.colors.white} />
);

const ParkDetailButtons = ({
  parkingWithApp,
}: {
  parkingWithApp?: boolean;
}) => {
  const { t } = useTranslation();
  const { navigate: navigatePark } = useNavigation<ParkNavProps>();
  const {
    buttonText,
    disabled,
    userLacksPrivateEvPriceAccess,
    isPricingLoading,
  } = useParkingButtonLogic();
  const { hasEVCharge, isLoadingParkZoneDetails } =
    useContext(ParkingContext) || {};
  const isResolvingParking = isLoadingParkZoneDetails || isPricingLoading;
  const { parkingConditions, setParkingConditions } = useParkingStore(
    (state) => state
  );
  const { selectedConnector } = useSearchStore((state) => state);
  const { selectedParkingZoneId } = parkingConditions;
  useParkingZoneChangeCountry();
  const { isLoggedIn, loginLoading } = useIsLoggedIn();

  const navigateToChargeOptions = useCallback(() => {
    useParkzoneStore.getState().setSelectedParkingZoneTab(undefined);
    if (disabled && !userLacksPrivateEvPriceAccess) {
      // Skipping the charge/park chooser must leave the same state its
      // "Charge only" button would have: the charger screen and the footer
      // below it both read this to know a charging session is being started.
      useParkzoneStore.getState().setSelectedParkingZoneTab('CHARGE');
      navigatePark('chargeSelect', { parkingZoneUid: selectedParkingZoneId });
    } else {
      navigatePark('chargeOptions', { parkingZoneUid: selectedParkingZoneId });
    }
  }, [
    navigatePark,
    selectedParkingZoneId,
    disabled,
    userLacksPrivateEvPriceAccess,
  ]);

  useEffect(() => {
    if (
      hasEVCharge &&
      selectedConnector &&
      !isResolvingParking &&
      (!disabled || !userLacksPrivateEvPriceAccess)
    ) {
      navigateToChargeOptions();
    }
  }, [
    selectedConnector,
    hasEVCharge,
    isResolvingParking,
    navigateToChargeOptions,
    disabled,
    userLacksPrivateEvPriceAccess,
  ]);

  useEffect(() => {
    if (
      hasEVCharge &&
      !isResolvingParking &&
      disabled &&
      !userLacksPrivateEvPriceAccess
    ) {
      navigateToChargeOptions();
    }
  }, [
    hasEVCharge,
    isResolvingParking,
    disabled,
    userLacksPrivateEvPriceAccess,
    navigateToChargeOptions,
  ]);

  const navigateToTimeSelection = () => {
    setParkingConditions({ endTime: new Date() });
    navigatePark('parkEndTime', { parkingZoneUid: selectedParkingZoneId });
  };

  if (!isLoggedIn) {
    return (
      <View>
        <ParkingSignInButton
          testID="park-zone-actions-login"
          loading={loginLoading}
          withContainer={false}
        />
      </View>
    );
  }

  const showParkButton = !(hasEVCharge && disabled);
  const showPersonalAccessRequired =
    !!hasEVCharge && userLacksPrivateEvPriceAccess && !isResolvingParking;

  return (
    <View>
      {showPersonalAccessRequired && (
        <StyledWrapper mb={showParkButton ? 20 : 0}>
          <Button
            buttonType="primary"
            size="l"
            disabled
            text={t('button.personalAccessRequired')}
            {...testIdentifiers(
              'park-zone-actions-personal-access-required-button'
            )}
          />
        </StyledWrapper>
      )}
      {hasEVCharge && !showPersonalAccessRequired && (
        <StyledWrapper mb={20}>
          <Button
            buttonType="quaternary"
            size="l"
            text={t('button.chargingOptions')}
            iconComponent={ChargeIconComponent}
            {...testIdentifiers('park-zone-actions-charging-options-button')}
            onPress={navigateToChargeOptions}
          />
        </StyledWrapper>
      )}
      {showParkButton && (
        <StyledWrapper mb={0}>
          <Button
            buttonType="primary"
            size="l"
            disabled={disabled}
            text={buttonText}
            iconComponent={disabled ? undefined : ParkIconComponent}
            {...testIdentifiers('park-zone-actions-park-only-button')}
            onPress={navigateToTimeSelection}
          />
        </StyledWrapper>
      )}
    </View>
  );
};

export default ParkDetailButtons;
